// Trigger Node Component

import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Schedule as ScheduleIcon,
  Webhook as WebhookIcon,
  TouchApp as ManualIcon,
  Email as EmailIcon,
  FolderOpen as FileIcon,
  Notifications as EventIcon,
  RadioButtonChecked as ActiveIcon
} from '@mui/icons-material';

interface TriggerNodeData {
  label: string;
  description?: string;
  triggerType?: 'webhook' | 'scheduled' | 'manual' | 'email' | 'file_watch' | 'event';
  status?: 'inactive' | 'active' | 'triggered' | 'error';
  lastTriggered?: string;
  triggerCount?: number;
  configuration?: any;
}

const TriggerNode: React.FC<NodeProps<TriggerNodeData>> = ({ 
  data, 
  selected, 
  id 
}) => {
  const getTriggerIcon = () => {
    switch (data.triggerType) {
      case 'webhook':
        return <WebhookIcon />;
      case 'scheduled':
        return <ScheduleIcon />;
      case 'manual':
        return <ManualIcon />;
      case 'email':
        return <EmailIcon />;
      case 'file_watch':
        return <FileIcon />;
      case 'event':
        return <EventIcon />;
      default:
        return <StartIcon />;
    }
  };

  const getStatusColor = () => {
    switch (data.status) {
      case 'active':
        return '#E8F5E9';
      case 'triggered':
        return '#E3F2FD';
      case 'error':
        return '#FFEBEE';
      default:
        return '#F5F5F5';
    }
  };

  const getBorderColor = () => {
    if (selected) return '#1976D2';
    switch (data.status) {
      case 'active':
        return '#4CAF50';
      case 'triggered':
        return '#2196F3';
      case 'error':
        return '#F44336';
      default:
        return '#9E9E9E';
    }
  };

  const getStatusIndicator = () => {
    switch (data.status) {
      case 'active':
        return (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#4CAF50',
              animation: 'pulse 2s ease-in-out infinite'
            }}
          />
        );
      case 'triggered':
        return (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#2196F3',
              animation: 'flash 1s ease-in-out infinite'
            }}
          />
        );
      case 'error':
        return (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#F44336'
            }}
          />
        );
      default:
        return (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#9E9E9E'
            }}
          />
        );
    }
  };

  const formatTriggerType = (type?: string) => {
    if (!type) return 'Trigger';
    return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Box sx={{ minWidth: 220 }}>
      <Card
        sx={{
          backgroundColor: getStatusColor(),
          border: `3px solid ${getBorderColor()}`,
          borderRadius: 3,
          minHeight: 100,
          transition: 'all 0.2s ease',
          boxShadow: selected ? 6 : 2,
          position: 'relative',
          overflow: 'visible',
          '&:hover': {
            boxShadow: 8,
            transform: 'translateY(-2px)'
          }
        }}
      >
        {/* Trigger pulse effect for start node */}
        {data.status === 'active' && (
          <Box
            sx={{
              position: 'absolute',
              top: -3,
              left: -3,
              right: -3,
              bottom: -3,
              borderRadius: 3,
              border: `2px solid ${getBorderColor()}40`,
              animation: 'ripple 2s infinite',
              pointerEvents: 'none'
            }}
          />
        )}

        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          {/* Header with Icon and Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: '50%',
                backgroundColor: `${getBorderColor()}20`,
                color: getBorderColor(),
                mr: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {getTriggerIcon()}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.2
                }}
              >
                {data.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem'
                }}
              >
                {formatTriggerType(data.triggerType)}
              </Typography>
            </Box>
            <Box sx={{ ml: 1 }}>
              {getStatusIndicator()}
            </Box>
          </Box>

          {/* Description */}
          {data.description && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 1.5,
                fontSize: '0.8rem',
                lineHeight: 1.3
              }}
            >
              {data.description}
            </Typography>
          )}

          {/* Status and Stats */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label={data.status || 'inactive'}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: getBorderColor(),
                color: 'white',
                textTransform: 'uppercase'
              }}
            />
            
            {data.triggerCount !== undefined && (
              <Chip
                label={`${data.triggerCount} triggers`}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            )}
          </Box>

          {/* Last triggered info */}
          {data.lastTriggered && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 1,
                fontSize: '0.7rem'
              }}
            >
              Last: {new Date(data.lastTriggered).toLocaleString()}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: getBorderColor(),
          width: 12,
          height: 12,
          border: '3px solid #fff',
          borderRadius: '50%',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}
      />

      {/* Triggered flash effect */}
      {data.status === 'triggered' && (
        <Box
          sx={{
            position: 'absolute',
            top: -6,
            right: -6,
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: '#2196F3',
            animation: 'flash 0.5s ease-in-out infinite',
            zIndex: 10
          }}
        />
      )}

      {/* Add animation keyframes */}
      <style>
        {`
          @keyframes pulse {
            0% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.1);
              opacity: 0.7;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
          
          @keyframes flash {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.3;
            }
          }
          
          @keyframes ripple {
            0% {
              transform: scale(1);
              opacity: 0.8;
            }
            100% {
              transform: scale(1.1);
              opacity: 0;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default memo(TriggerNode);